import { tmxApiCall } from './index'
import { RegexConstants } from '../constants.js'

// Utilities
import Logger from '../utility/loggerUtil'
import Session from '../utility/sessionUtil'

export default {
  async getLoanSummaries (brandName) {
    Logger.info('Started Get Loan Summaries query.')
    return tmxApiCall('get', '/accounts', {
      headers: {
        'x-mobile-app-id-token': Session.getIdToken()
      },
      params: {
        brandName
      }
    })
  },

  async getLoanDetail (loanId, lmsId) {
    Logger.info('Started Get Loan Detail query.')
    return tmxApiCall('get', '/accountdetail', {
      headers: {
        'x-mobile-app-id-token': Session.getIdToken()
      },
      params: {
        loanId,
        lmsId
      }
    })
  },

  async getAccountHistory (loanId, storeId, lmsId) {
    Logger.info('Started Account History query.')
    return tmxApiCall('get', '/accounthistory', {
      params: {
        loanId,
        storeId,
        lmsId
      }
    })
  },

  async associate (phoneNumber, dob, ssn, loanId, cognitoUserId) {
    Logger.info('Started associate loan query.')
    const day = dob.getDate()
    const monthIndex = dob.getMonth()
    const year = dob.getFullYear()

    return tmxApiCall('get', '/customer', {
      params: {
        phoneNumber: phoneNumber.replace(RegexConstants.phoneNumberFormatting, ''),
        dateOfBirth: year + ('0' + (monthIndex + 1)).slice(-2) + ('0' + day).slice(-2),
        ssn,
        loanId,
        cognitoUserId
      }
    })
  },

  /**
   * Gets the current active duty military status of the customer.
   * @param {String} customerId - The customer identifier.
   * @param {String} lmsId - The LMS identifier.
   * @param {String} loanId - The Loan identifier.
   * @param {Boolean} migrateProduct - Whether or not the user is migrating products
   * @param {Boolean} isDisbursement - Whether or not the user is disbursing.
   * @returns {Promise<{isActiveDuty: Number}>} The customers active duty military status (0 means not active).
   */
  async getMlaStatus (customerId, lmsId, loanId, migrateProduct, isDisbursement) {
    Logger.info('Started MLA Status query.')
    return tmxApiCall('get', '/militarystatus', {
      params: {
        customerId,
        lmsId,
        loanId,
        migrateProduct,
        isDisbursement
      }
    })
  },

  /**
   * Updates NLS future reapply date if customer self reports they are military.
   * @param {String} customerId - The customer identifier.
   * @param {String} lmsId - The LMS identifier (Should be NLS).
   */
  async updateMlaStatus (customerId, lmsId) {
    Logger.info('Started MLA Status update.')
    return tmxApiCall('post', '/militarystatus', {
      customerId,
      lmsId
    })
  },

  async sendPaymentConfirmation (confirmationObj) {
    Logger.info('Started payment confirmation request.')
    return tmxApiCall('post', '/sendConfirmation', {
      profileId: confirmationObj.profileId,
      email: confirmationObj.email,
      phoneNumber: confirmationObj.phoneNumber,
      cardHolderName: confirmationObj.cardHolderName,
      loanId: confirmationObj.loanId,
      confirmationNumber: confirmationObj.confirmationNumber,
      amountPaid: parseFloat(confirmationObj.amountPaid).toFixed(2).replace(RegexConstants.currencyFormatting, '$1,'),
      paymentDate: confirmationObj.paymentDate,
      cardNumber: confirmationObj.cardNumber.substring(confirmationObj.cardNumber.length - 4),
      customerName: confirmationObj.customerName,
      storePhoneNumber: confirmationObj.storePhoneNumber,
      productBrand: confirmationObj.productBrand,
      collateralYear: confirmationObj.collateralYear,
      collateralMake: confirmationObj.collateralMake,
      collateralModel: confirmationObj.collateralModel,
      disbursementFlow: confirmationObj.disbursementFlow,
      disburseCardNumber: confirmationObj.disburseCardNumber,
      isRefi: confirmationObj.isRefi,
      newLoanId: confirmationObj.newLoanId,
      newLoanDueDate: confirmationObj.newLoanDueDate,
      newPrincipalBalance: confirmationObj.newPrincipalBalance,
      messageType: confirmationObj.messageType,
      paymentDueDate: confirmationObj.paymentDueDate,
      isVoidRefi: confirmationObj.isVoidedRefi,
      alternatePhoneNumber: confirmationObj.isVoidedRefi ? undefined : confirmationObj.alternatePhoneNumber,
      alternateEmail: confirmationObj.isVoidedRefi ? undefined : confirmationObj.alternateEmail,
      isPayoff: confirmationObj.isPayoff === true,
      disbursementAmount: confirmationObj.disbursementAmount,
      disbursementType: confirmationObj.disbursementType,
      appointmentDateTime: confirmationObj.appointmentDateTime,
      isPartial: confirmationObj.isPartial === true,
      lmsId: confirmationObj.lmsId,
      migrateProduct: confirmationObj.migrateProduct
    })
  },

  async cancelRenewal (loanHeader, confirmationObj) {
    Logger.info('Started cancel renewal post')
    return tmxApiCall('post', '/cancelrefinance', {
      newLoanHeaderId: loanHeader,
      confirmationData: {
        profileId: confirmationObj.profileId,
        email: confirmationObj.email,
        phoneNumber: confirmationObj.phoneNumber,
        customerId: confirmationObj.customerId,
        cardHolderName: confirmationObj.cardHolderName,
        loanId: confirmationObj.loanId,
        confirmationNumber: confirmationObj.confirmationNumber,
        amountPaid: parseFloat(confirmationObj.amountPaid).toFixed(2).replace(RegexConstants.currencyFormatting, '$1,'),
        paymentDate: confirmationObj.paymentDate,
        cardNumber: confirmationObj.cardNumber.substring(confirmationObj.cardNumber.length - 4),
        customerName: confirmationObj.customerName,
        storePhoneNumber: confirmationObj.storePhoneNumber,
        productBrand: confirmationObj.productBrand,
        collateralYear: confirmationObj.collateralYear,
        collateralMake: confirmationObj.collateralMake,
        collateralModel: confirmationObj.collateralModel,
        paymentDueDate: confirmationObj.paymentDueDate,
        loanSigned: confirmationObj.loanSigned,
        isVoidRefi: true
      }
    })
  },

  async getCustomerMessages (customerId) {
    Logger.info('Started Get Customer Messages query.')
    return tmxApiCall('get', '/dashboardmessage', {
      params: {
        customerId
      }
    })
  },

  async unenrollAch (loanId) {
    Logger.info('Started Unenroll ACH query.')
    return tmxApiCall('post', '/enrollment', {
      loanId
    })
  },

  async getNewAccountTerms (loanId, productId, storeId, disbursementAmount, lmsId, guestPayment, migrateProduct) {
    Logger.info('Started Account Terms query.')
    return tmxApiCall('get', '/newaccountterms', {
      params: {
        loanId,
        productId,
        storeId,
        disbursementAmount,
        lmsId,
        guestPayment,
        migration: migrateProduct
      }
    })
  },

  /**
   * Gets the loan application status for any loans tied to the user's LMS Customer IDs.
   */
  async getLoanApplicationStatus () {
    Logger.info('Started Application Status query.')
    return tmxApiCall('get', '/applicationstatus', {
      headers: {
        'x-mobile-app-id-token': Session.getIdToken()
      }
    })
  },

  /**
   * Withdraws an application.
   * @param {String} applicationId - The application ID to withdraw.
   * @param {String} lmsId - The identifier for the LMS that the loan is associated with.
   */
  async withdrawApplication (applicationId, lmsId) {
    Logger.info('Started Withdraw Application call')
    return tmxApiCall('put', '/applicationstatus', {
      applicationId,
      lmsId
    })
  },

  /**
   * Gets signed documents associated with a loan.
   * @param {String} lmsId - The identifier for the LMS that the loan is associated with.
   * @param {String} loanId - The LMS identifier for the loan.
   * @param {Boolean} paidOff - Is the loan paid off or not?
   * @returns {Promise<[{label: String, url: String}]>} A collection of document objects.
   */
  async getLoanDocs (lmsId, loanId, paidOff) {
    Logger.info('Started Get Loan Docs')
    return tmxApiCall('get', '/loanDocuments', {
      headers: {
        'x-mobile-app-id-token': Session.getIdToken()
      },
      params: {
        lmsId,
        loanId,
        paidOff
      }
    })
  },

  /**
   * Sends spouse address to service layer.
   * @param {String} customerId - The identifier of the customer that the loan is associated with.
   * @param {Object} address - The address object which should contain, address, city, state, zip.
   * @param {String} firstName - The first name of the spouse.
   * @param {String} lastName - The last name of the spouse.
   * @returns {Promise<{success: Boolean}>}
   */
  async sendSpousalNotification (customerId, address, firstName, lastName) {
    Logger.info('Started Post Spouse Address')
    return tmxApiCall('post', '/spouseAddress', {
      customerId,
      address,
      firstName,
      lastName
    })
  },

  /**
   * Gets the ancillary products for the loan.
   * @param {String} loanId - The unique identifier for the loan.
   * @param {Number} paymentAmount - The amount that the payment is for.
   * @param {Number} productId - The product ID of the loan.
   * @param {Number} storeId - The 4 digit store ID for the loan.
   * @returns {Promise<{products: Array}>} The insurance products associated to the loan.
   */
  async getAncillaryProducts (loanId, paymentAmount, productId, storeId) {
    Logger.info('Started Get Ancillary Products')
    return tmxApiCall('get', '/ancillaryProducts', {
      params: {
        loanId,
        productId,
        paymentAmount,
        storeId
      }
    })
  },

  /**
   * Draws funds from a line of credit.
   * @param {String} loanId - The unique identifier for the loan.
   * @param {Number} storeId - The 4 digit store ID for the loan.
   * @param {Number} disbursementAmount - The amount to be drawn from the loan.
   * @param {String} [disburseCardId] - The token representing the disbursement debit card to push funds to. Only used for push to debit disbursements.
   * @param {Number} disbursementType - A single digit number, defined by DisbursementTypeEnum in constants.js.
   * @param {String} lmsId - The identifier for the LMS that the loan is associated with.
   * @param {String} username - The email or phone number the customer is signed in with.
   * @param {String} guestPayment - If the user did the draw from a guest link or not.
   * @returns {Promise<{confirmationNumber: String, newLoanId: String, newPrincipalBalance: Number, newLoanDueDate: String}>}
   */
  async draw (loanId, storeId, disbursementAmount, disburseCardId, disbursementType, lmsId, username, guestPayment) {
    Logger.info('Started draw for disbursement')
    return tmxApiCall('post', '/draw', {
      loanId,
      storeId,
      disbursementAmount,
      disburseCardId,
      disbursementType,
      lmsId,
      username,
      guestPayment
    })
  },

  /**
   * Gets the post draw response from /draw.
   * @param {String} messageKey - The key associated with the draw.
   * @returns {Promise<{success: Boolean, Error: String, confirmationNumber: String, newLoanId: String, newLoanDueDate: String, newPrincipalBalance: Number}>} Payment result information.
   */
  async getDrawStatus (messageKey) {
    Logger.info('Started Check Post Draw status query.')
    return tmxApiCall('get', '/draw', {
      params: {
        messageKey
      }
    })
  },

  /**
   * Cancels a pending cash advance.
   * @param {String} loanId - The unique identifier for the loan.
   * @param {String} lmsId - The identifier for the LMS that the loan is associated with.
   * @param {Number} transactionId - The unique identifier for the cash advance on the loan.
   */
  async cancelCashAdvance (loanId, lmsId, transactionId) {
    Logger.info('Started cancel of cash advance')
    return tmxApiCall('delete', '/draw', {
      data: {
        loanId,
        lmsId,
        transactionId
      }
    })
  },

  /**
   * Gets repo freeze status based on the loan's collateral VIN.
   * @param {String} loanId - The unique identifier for the loan.
   * @returns {Promise<{frozen: Boolean}>}
   */
  async getRepoInfo (loanId) {
    Logger.info('Started get repo info')
    return tmxApiCall('get', '/repoInfo', {
      params: {
        loanId
      }
    })
  },

  /**
   * Sets the repo freeze status on the collateral associated with the passed in loan.
   * @param {String} loanId - The unique identifier for the loan.
   */
  async setRepoFreeze (loanId) {
    Logger.info('Started set repo freeze')
    return tmxApiCall('put', '/repoInfo', {
      loanId
    })
  },

  /**
   * Gets current settlement offer status details.
   * @param {String} loanId - The unique identifier for the loan.
   * @returns {Promise<{status: {docExp: String, docSigned: Boolean, paymentMade: Boolean}}>}
   */
  async getSettlementStatus (loanId) {
    Logger.info('Started get settlement offer status')
    return tmxApiCall('get', '/settlementStatus', {
      params: {
        loanId
      }
    })
  },

  /**
   * Sets the status of a settlement offer.
   * @param {String} loanId - The unique identifier for the loan.
   * @param {Boolean} [docSigned] - True if the settlement documents have been signed. Leave undefined if only updating pay flag.
   * @param {Boolean} [paymentMade] - True if the settlement payment was successful. Leave undefined if only updating doc signing flag.
   */
  async setSettlementStatus (loanId, docSigned = undefined, paymentMade = undefined) {
    Logger.info('Started set settlement offer status')
    return tmxApiCall('post', '/settlementStatus', {
      loanId,
      docSigned,
      paymentMade
    })
  },

  /**
   * Gets details on which types of risk details are applicable for the customer and loan.
   * @param {Boolean} isAddon - True if the refinance is part of an addon or not.
   * @param {String} loanId - The unique identifier for the loan.
   * @param {String} migrateProduct - True if a product migration was chosen.
   * @returns {Promise<{status: {rateChanged: Boolean, creditLimitChanged: Boolean, refinanceDenied: Boolean}}>} The risk details for the customer and loan.
   */
  async getRiskInfo (isAddon, loanId, migrateProduct) {
    Logger.info('Started get risk info.')
    return tmxApiCall('get', '/riskInfo', {
      params: {
        isAddon,
        loanId,
        migrateProduct
      }
    })
  }
}
